// How the "Recent messages in mailbox" panel words a sampled message's
// outcome. The API (api/src/sageEmailOutcome.js) decides WHETHER a message
// imported — this only turns that answer into a label and a tone, so the two
// sides cannot disagree about the fact, only about its wording.
//
// Tones: 'success' — a log row exists and the import completed;
//        'pending' — a log row exists and the run is still queued/processing;
//        'error'   — a log row exists and the import failed;
//        'warn'    — no log row and the message could have imported (read-skip);
//        'info'    — no log row for a reason that is not a fault (unread, no
//                    attachment, no import type);
//        'unknown' — the API could not look the outcome up (outcome === null).

const FAILED   = new Set(['failed', 'error']);
const SETTLED  = new Set(['completed', 'reverted']);

const trunc = (s, n) => {
  const t = String(s || '').replace(/\s+/g, ' ').trim();
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
};

const fmtRows = (o) => {
  if (o.rowsSucceeded === null || o.rowsSucceeded === undefined) return '';
  const n = Number(o.rowsSucceeded);
  if (!Number.isFinite(n)) return '';
  return `, ${n.toLocaleString()} row${n === 1 ? '' : 's'}`;
};

// Returns { text, tone }. `m` is one sample entry as the diagnostics endpoint
// returns it (camel-cased by the API client): importType, hasAttachments,
// isRead, outcome.
export function describeOutcome(m) {
  const type = m?.importType || (m?.hasAttachments ? 'unclassified' : 'no attachment');
  const o = m?.outcome;
  if (o === null || o === undefined) {
    return { text: `${type} · outcome unknown`, tone: m?.importType ? 'unknown' : 'info' };
  }
  if (o.imported) {
    const status = String(o.status || '').toLowerCase();
    if (FAILED.has(status)) {
      const why = o.errorSummary ? `: ${trunc(o.errorSummary, 60)}` : '';
      return { text: `${type} · Failed${why}`, tone: 'error' };
    }
    if (SETTLED.has(status)) {
      return { text: `${type} · imported (${o.status}${fmtRows(o)})`, tone: 'success' };
    }
    return { text: `${type} · ${o.status || 'queued'}`, tone: 'pending' };
  }
  const reason = o.reason || 'not imported';
  // The one reason that is a fault somebody can fix: it could have imported
  // and did not, because it was read first. Everything else is benign.
  const isReadSkip = /read before processing/i.test(reason);
  const short = isReadSkip ? 'skipped — read before processing' : reason;
  return { text: m?.importType ? `${type} · ${short}` : type, tone: isReadSkip ? 'warn' : 'info' };
}
